import { Person } from "@/features/people/graphql/generated";
import { Column, Row } from "@/shared/ui/Blocks";
import { SubtitleText } from "@/shared/ui/Typography";
import { Card } from "@/shared/ui/Card";
import { IconButton, IconButtonProps } from "@/shared/ui/IconButton";
import { Title } from "@/shared/ui/Title";
import { ProgressIndicator } from "@/shared/widgets/ProgressIndicatorCTA";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { useRemindPeople } from "@/features/people/hooks/useRemindPeople";
import { useContactInvite } from "@/features/people/hooks/useSendContactInvite";
import { useResendInvite } from "@/features/people/hooks/useResendInvite";
import { AccessLevel } from "@/shared/constants";
import { cn } from "@/shared/utils/shadcn";
import { filter, find, includes, map, toLower } from "lodash";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ResourceEnum } from "@/shared/constants/resources";
import { useAssessmentSurveys } from "@/features/profile/hooks/useAssessmentSurveys";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import { AssessmentOrder } from "../../graphql/generated";

interface ActionRowConfig {
  title: string;
  subtitle?: string;
  buttonLabel: string;
  buttonIcon: IconButtonProps["icon"];
  buttonVariant?: IconButtonProps["variant"];
  onAction: () => void;
}

interface ActionItemProps {
  config: ActionRowConfig;
  className?: string;
}

const ActionItem = ({ config, className }: ActionItemProps) => (
  <Row className={cn("gap-4 flex-wrap items-center", className)}>
    <Column>
      <SubtitleText color="white" className="font-semibold">
        {config.title}
      </SubtitleText>
      {config.subtitle && (
        <SubtitleText color="lightGrey" size="xsmall">
          {config.subtitle}
        </SubtitleText>
      )}
    </Column>
    <Row>
      <IconButton
        variant={config.buttonVariant}
        className="flex-row-reverse"
        size="md"
        color="darkGrey"
        onClick={config.onAction}
        label={config.buttonLabel}
        icon={config.buttonIcon}
      />
    </Row>
  </Row>
);

const ACTION_CONTAINER_CLASSES = "mt-8 border-t border-grey-700 pt-8";

interface ProfileEmptyStateProps {
  variant?: "team" | "user" | "person";
  context?: "candidate" | "member_in_team" | "org_member";
  person?: Person;
  surveyConfig?: AssessmentSurveyType[];
  projectId?: number;
  className?: string;
  visible?: boolean;
}

const ProfileEmptyState = (props: ProfileEmptyStateProps) => {
  const {
    variant,
    context,
    person,
    className,
    visible,
    projectId,
    surveyConfig,
  } = props;
  const { handleSendProfileReminder } = useRemindPeople();
  const { handleResendInvite } = useResendInvite();
  const { handleSendContactInvite } = useContactInvite();

  const { validateAccessToResource } = useViewerPermissions();
  const { handleRetakeSurvey, loading } = useAssessmentSurveys();

  const { t } = useTranslation();

  const isAdminViewer = validateAccessToResource({
    resourceType: ResourceEnum.organization,
    accessLevel: AccessLevel.edit,
  });

  const contextLabel = useMemo(() => {
    if (context === "candidate") {
      return t("profile.context.candidate");
    }

    if (context === "member_in_team") {
      return t("profile.context.team_member");
    }
    return t("profile.context.org_member");
  }, [context, t]);

  const texts = useMemo(() => {
    const count = surveyConfig?.length;
    if (variant === "team") {
      return {
        title: t("profile.empty_state_team"),
        description: t("profile.empty_state_team_description", {
          count,
        }),
      };
    }
    if (variant === "person") {
      return {
        title: t("profile.empty_state_person", { context: contextLabel }),
        description: t("profile.empty_state_person_description", {
          name: person?.name || toLower(contextLabel),
          count,
        }),
      };
    }

    return {
      title: t("profile.empty_state_user"),
      description: t("profile.empty_state_user_description", {
        count,
      }),
    };
  }, [person, variant, contextLabel, surveyConfig, t]);

  const tasksCount = useMemo(() => {
    return {
      total: surveyConfig?.length || 0,
      completed: filter(surveyConfig, (sc) =>
        includes(person?.completed_dim_types, sc)
      ).length,
    };
  }, [surveyConfig, person?.completed_dim_types]);

  const handleAssessmentSurveyNavigation = (
    surveyType?: AssessmentSurveyType
  ) => {
    if (variant !== "user" || !surveyType) return;

    const pendingOrder = find(
      person?.assessment_orders,
      (ao: AssessmentOrder) => {
        ao.assessment_type === surveyType && !ao.completed_at;
      }
    ) as AssessmentOrder;

    handleRetakeSurvey(surveyType, pendingOrder?.assessment_link);
  };

  const surveys = useMemo(() => {
    return map(surveyConfig, (surveyType: AssessmentSurveyType) => ({
      survey_type: surveyType,
      title: t(`survey.${toLower(surveyType || "")}`),
      estimate: t(`survey.${toLower(surveyType || "")}_estimate`),
      completed: includes(person?.completed_dim_types, surveyType),
    }));
  }, [person, surveyConfig, t]);

  const personActionConfig = useMemo((): ActionRowConfig | null => {
    if (variant !== "person" || !isAdminViewer) return null;

    if (person?.is_contact) {
      return {
        title: t("profile.empty_state_contact_invite"),
        buttonLabel: t("profile.action_send_invite"),
        buttonIcon: "Send",
        onAction: () => handleSendContactInvite(person?.id!, projectId!),
      };
    }

    if (person?.invite) {
      return {
        title: t("profile.person_invite_not_accepted"),
        buttonLabel: t("profile.action_resend_invite"),
        buttonIcon: "Send",
        onAction: () => handleResendInvite({ personId: person?.id! }),
      };
    }

    return {
      title: t("profile.empty_state_person_reminder"),
      buttonLabel: t("profile.action_send_reminder"),
      buttonIcon: "Send",
      onAction: () => handleSendProfileReminder(person?.id!),
    };
  }, [
    variant,
    isAdminViewer,
    person,
    projectId,
    handleSendContactInvite,
    handleResendInvite,
    handleSendProfileReminder,
    t,
  ]);

  const surveyActionConfigs = useMemo((): ActionRowConfig[] => {
    if (variant !== "user") return [];

    return map(surveys, (survey) => ({
      title: survey.title,
      subtitle: survey.estimate,
      buttonLabel: survey.completed
        ? t("profile.action_retake")
        : t("profile.action_start"),
      buttonIcon: survey.completed ? "Refresh" : "Arrow",
      buttonVariant: survey.completed ? "passive" : "default",
      onAction: () => handleAssessmentSurveyNavigation(survey.survey_type),
    })) as ActionRowConfig[];
  }, [variant, surveys, handleAssessmentSurveyNavigation, t]);

  const renderSurveyActions = () => {
    if (personActionConfig) {
      return (
        <ActionItem
          config={personActionConfig}
          className={ACTION_CONTAINER_CLASSES}
        />
      );
    }

    if (surveyActionConfigs.length > 0) {
      return (
        <Row
          className={cn(
            "justify-between gap-4 overflow-x-auto",
            ACTION_CONTAINER_CLASSES
          )}
        >
          {surveyActionConfigs.map((config, index) => (
            <ActionItem key={index} config={config} />
          ))}
        </Row>
      );
    }

    return null;
  };

  if (!visible || import.meta.env.VITE_GPT_FLOW_CONFIG === "rraqa") return null;

  return (
    <Card variant="dark" className={cn("p-8", className)}>
      <Row>
        <Column>
          <Title as="h6" color="white" className="font-semibold">
            {texts.title}
          </Title>
          <SubtitleText color="white">{texts.description}</SubtitleText>
        </Column>
        {tasksCount.total > 0 && (
          <Row className="gap-4 self-start flex-1 justify-end items-center flex-wrap min-w-[100px] hidden">
            <SubtitleText color="white">
              {t("common.x_of_y", {
                completed: tasksCount.completed,
                total: tasksCount.total,
              })}
            </SubtitleText>
            <ProgressIndicator
              totalCount={tasksCount.total}
              completedCount={tasksCount.completed}
              color="green"
            />
          </Row>
        )}
      </Row>
      {renderSurveyActions()}
    </Card>
  );
};

export default ProfileEmptyState;
